import { useEffect, useRef } from "react";


const IDLE_TIMEOUT_MS = 24 * 60 * 60 * 1000;
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;
const IDLE_CHECK_INTERVAL_MS = 60 * 1000;
const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "scroll", "touchstart"];

function isCurrentArea(area) {
  const inPortal = window.location.pathname.startsWith("/portal");
  return area === "portal" ? inPortal : !inPortal;
}

export function useIdleLogout({ authenticated, area, heartbeat, onLogout, loginPath }) {
  const lastActivityRef = useRef(Date.now());
  const lastHeartbeatRef = useRef(Date.now());
  const heartbeatRef = useRef(heartbeat);
  const logoutRef = useRef(onLogout);

  useEffect(() => {
    heartbeatRef.current = heartbeat;
    logoutRef.current = onLogout;
  });

  useEffect(() => {
    if (!authenticated) return undefined;

    lastActivityRef.current = Date.now();
    lastHeartbeatRef.current = Date.now();
    let heartbeatRunning = false;
    let logoutRunning = false;

    const redirectToLogin = async (reason) => {
      if (logoutRunning) return;
      logoutRunning = true;
      try {
        await logoutRef.current();
      } finally {
        if (isCurrentArea(area)) {
          window.location.replace(`${loginPath}?reason=${reason}`);
        }
      }
    };

    const sendHeartbeat = async () => {
      if (heartbeatRunning || logoutRunning || !isCurrentArea(area)) return;
      heartbeatRunning = true;
      lastHeartbeatRef.current = Date.now();
      try {
        await heartbeatRef.current();
      } catch (error) {
        if (error.response?.status === 401) {
          await redirectToLogin("session-expired");
        }
      } finally {
        heartbeatRunning = false;
      }
    };

    const markActivity = () => {
      if (!isCurrentArea(area)) return;
      const now = Date.now();
      lastActivityRef.current = now;
      if (now - lastHeartbeatRef.current >= HEARTBEAT_INTERVAL_MS) {
        void sendHeartbeat();
      }
    };

    const checkIdleTime = () => {
      if (!isCurrentArea(area)) return;
      if (Date.now() - lastActivityRef.current >= IDLE_TIMEOUT_MS) {
        void redirectToLogin("inactive");
      }
    };

    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, markActivity, { passive: true });
    }
    const timer = window.setInterval(checkIdleTime, IDLE_CHECK_INTERVAL_MS);

    return () => {
      window.clearInterval(timer);
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, markActivity);
      }
    };
  }, [authenticated, area, loginPath]);
}
